import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import MarkChatUnreadOutlinedIcon from "@mui/icons-material/MarkChatUnreadOutlined";
import { useTheme } from "@mui/material/styles";
import toastError from "../../errors/toastError";
import Popover from "@mui/material/Popover";
import useMediaQuery from "@mui/material/useMediaQuery";
import ResponsiveSheet from "../../components/ResponsiveSheet";
import {
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  Typography,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import api from "../../services/api";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";

import notifySound from "../../assets/chat_notify.mp3";
import useSound from "use-sound";
import { i18n } from "../../translate/i18n";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";


const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

export default function ChatPopover(volume) {
  const theme = useTheme();
  // RDS-FIX-NOTIFICATIONS-MOBILE: mismo fix ya aplicado en
  // NotificationsPopOver/AnnouncementsPopover -- en móvil el Popover
  // anclado se cortaba/montaba sobre el topbar (44px). Popover se
  // mantiene igual en desktop. Este panel es el chat INTERNO entre
  // agentes de la empresa (mismo feature de useNavGroups.js "/chats",
  // "Comunicación del equipo") -- no soporte de Shessai ni nada externo.
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  //   const socketManager = useContext(SocketContext);
  const { user: loggedUser, socket } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(true);
  const { datetimeToClient } = useDate();
  const [play] = useSound(notifySound, volume);
  const soundAlertRef = useRef();

  useEffect(() => {
    soundAlertRef.current = play;

    if (!("Notification" in window)) {
      
    } else {
      Notification.requestPermission();
    }
  }, [play]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  // RDS-SOCKET-SHAPE-FIX2: este efecto creaba una SEGUNDA conexion con el
  // shape roto { companyId, userId } (el mismo bug ya corregido en
  // Chat/index.js) cuando el socket correcto YA estaba disponible via
  // AuthContext (linea 102). Como SocketWorker es singleton, si esta llamada
  // rota corria antes que la de useAuth.js, envenenaba el socket compartido
  // de toda la sesion con companyId/userId vacios -- de ahi el
  // "userId=undefined" en la conexion socket.io, incluso con Chat/index.js
  // ya arreglado. Ahora reutiliza el socket del context (mismo patron de
  // guard que companyId en useNavGroups.js: espera a que este listo).
  useEffect(() => {
    if (loggedUser.id && socket) {
      const companyId = loggedUser.companyId;

      const onCompanyChatPopover = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
          
          if (
            data.newMessage.senderId !== loggedUser.id &&
            data.chat.users.find((user) => user.userId === loggedUser.id)
          ) {
            soundAlertRef.current();

            // Check if notifications are permitted
            if (Notification.permission === "granted") {
              const notification = new Notification(
                "Chat interno #" + data.newMessage.chatId,
                {
                  body: `${data.newMessage.sender.name}: ${
                    data.newMessage.message
                  } - ${format(new Date(), "HH:mm")}`,
                  icon: data.newMessage.sender.profileImage,
                }
              );

              notification.onclick = (e) => {
                e.preventDefault();
                window.focus();
                navigate(`/chats/${data.newMessage.chatId}`);
              };
            }
          }
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatPopover);

      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatPopover);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser.id, loggedUser.companyId, socket]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === loggedUser.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, loggedUser.id]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goToMessages = (chat) => {
    window.location.href = `/chats/${chat.uuid}`;
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // RDS-FIX-NOTIFICATIONS-MOBILE: mismo contenido para las dos ramas --
  // Popover en desktop (sin cambios) y ResponsiveSheet en móvil, ver
  // abajo. Nada de lógica de datos/estado/sonido se tocó, solo el
  // contenedor.
  const chatListContent = (
    <Paper
      variant="outlined"
      onScroll={handleScroll}
      sx={theme => ({ flex: 1, maxHeight: 300, maxWidth: 500, padding: theme.spacing(1), overflowY: "scroll", ...theme.scrollbarStyles, borderRadius:"12px", border: theme.palette.mode==="dark" ? "0.5px solid rgba(255,255,255,0.10)" : "0.5px solid rgba(0,0,0,0.08)", boxShadow: theme.palette.mode==="dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.10)" })}
    >
      <List
        component="nav"
        aria-label="main mailbox folders"
        style={{ minWidth: 300 }}
      >
        {isArray(chats) &&
          chats.map((item, key) => (
            <ListItem
              key={key}
              style={{
                background: key % 2 === 0
                  ? (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#ededed")
                  : (theme.palette.mode === "dark" ? "rgba(255,255,255,0.01)" : "white"),
                border: theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eee",
                cursor: "pointer",
              }}
              onClick={() => goToMessages(item)}
              button
            >
              <ListItemText
                primary={
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      component="span"
                      style={{ fontWeight: "bold", fontSize: 13 }}
                    >
                      {typeof item.lastMessage === "string"
                        ? item.title || "Chat"
                        : item.lastMessage?.sender?.name ||
                          item.title ||
                          "Chat"}
                    </Typography>
                    <Typography
                      component="span"
                      style={{ fontSize: 12, color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.5)" : "#666" }}
                    >
                      {typeof item.lastMessage === "string"
                        ? item.lastMessage
                        : item.lastMessage?.message || "Sin mensajes"}
                    </Typography>
                  </div>
                }
                secondary={
                  <Typography component="span" style={{ fontSize: 12 }}>
                    {datetimeToClient(item.updatedAt)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        {isArray(chats) && chats.length === 0 && (
          <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", py: "28px", px: "16px", gap: "8px",
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "10px",
              background: theme.palette.mode === "dark" ? "rgba(3,153,255,0.12)" : "#eff6ff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ChatOutlinedIcon sx={{ fontSize: 18, color: "#0399FF" }} />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: theme.palette.mode === "dark" ? theme.palette.text.primary : "rgba(0,0,0,0.55)" }}>
              Sin chats
            </Typography>
            <Typography sx={{ fontSize: 11, color: theme.palette.mode === "dark" ? theme.palette.text.secondary : "rgba(0,0,0,0.35)", textAlign: "center" }}>
              Los chats internos aparecerán aquí
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  );

  return (
    <div>
      <IconButton
        aria-describedby={id}
        variant="contained"
        color={invisible ? "default" : "inherit"}
        onClick={handleClick}
        size="medium"
        sx={{ color: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)" }}
      >
        <Badge
          variant="dot"
          invisible={invisible}
          sx={{
            "& .MuiBadge-dot": {
              backgroundColor: "#a78bfa",
              animation: invisible ? "none" : "dotPulse 2s cubic-bezier(0.16,1,0.3,1) infinite",
            },
            "@keyframes dotPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
          }}
        >
          <MarkChatUnreadOutlinedIcon sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>

      {isMobileLayout ? (
        <ResponsiveSheet open={open} onClose={handleClose}>
          {chatListContent}
        </ResponsiveSheet>
      ) : (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          {chatListContent}
        </Popover>
      )}
    </div>
  );
}
